
import Layout from '@/layout/index.vue';
import type { RouteRecordRaw } from 'vue-router'
import { roterPre } from '@/settings';

/**
 * 活动管理（商户端）
 * 活动商品 Ticket + 自营场地 Place
 * 菜单 route 对齐：/activity 下的控制台、供应、场地、合约、档期、排期
 */
const activityRouter: RouteRecordRaw = {
  path: `${roterPre}/activity`,
  name: 'activity',
  component: Layout,
  alwaysShow: true,
  // 侧栏以 ti_eb_system_menu「活动」为准；此处仅注册路由与面包屑标题
  meta: {
    icon: 's-ticket',
    title: '活动',
  },
  children: [
    {
      path: 'list',
      name: 'activityList',
      meta: {
        title: '活动列表',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      component: () => import('@/views/_expand/activityList/index.vue'),
    },
    {
      path: 'menu',
      name: 'activityShopMenu',
      meta: {
        title: '供应',
        noCache: true,
        // 菜单隐藏：高亮指到已显示的活动控制台，侧栏才能回到「活动」
        activeMenu: `${roterPre}/activity/desk`,
      },
      component: () => import('@/views/_expand/activityList/shopMenu.vue'),
    },
    {
      path: 'form',
      name: 'activityQuestionnaire',
      meta: {
        title: '问卷管理',
        noCache: true,
        activeMenu: `${roterPre}/activity`,
      },
      component: () => import('@/views/_expand/activityForm/index.vue'),
    },
    {
      path: 'form/edit',
      name: 'activityQuestionnaireEdit',
      meta: {
        title: '编辑问卷',
        noCache: true,
        // 问卷管理是隐藏菜单，不在侧栏树里；高亮指到可见的「活动」
        activeMenu: `${roterPre}/activity`,
      },
      hidden: true,
      component: () => import('@/views/_expand/activityForm/edit.vue'),
    },
    {
      path: 'reviews',
      name: 'activityReviews',
      meta: {
        title: '活动评价',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      // 独立页：不影响 EB 商品评价 views/product/Reviews
      component: () => import('@/views/_expand/activityList/reviews.vue'),
    },
    {
      path: 'list/addActivity/:id?/:edit?',
      name: 'AddActivity',
      component: () => import('@/views/_expand/addActivity/index.vue'),
      meta: {
        title: '活动添加',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'desk',
      name: 'activityDesk',
      component: () => import('@/views/_expand/activityList/desk.vue'),
      meta: {
        title: '本场控制台',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'list/registration',
      name: 'activityRegistration',
      component: () => import('@/views/_expand/activityList/registration.vue'),
      meta: {
        title: '报名管理',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'list/open-checkin',
      name: 'activityOpenCheckin',
      component: () => import('@/views/_expand/activityList/openCheckin.vue'),
      meta: {
        title: '开放场签到',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'list/pair',
      name: 'activityPairSession',
      component: () => import('@/views/_expand/activityList/pairSession.vue'),
      meta: {
        title: '场次配对',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'list/vote',
      name: 'activityVoteSession',
      component: () => import('@/views/_expand/activityList/voteSession.vue'),
      meta: {
        title: '场次投票',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'list/lottery',
      name: 'activityTicketLottery',
      component: () => import('@/views/_expand/activityList/ticketLottery.vue'),
      meta: {
        title: '票档抽选',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'list/treat',
      name: 'activityTreatShelf',
      component: () => import('@/views/_expand/activityList/treatShelf.vue'),
      meta: {
        title: '场内心意',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'list/menu',
      name: 'activityEventMenu',
      component: () => import('@/views/_expand/activityList/eventMenu.vue'),
      meta: {
        title: '本场菜单',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'list/invite',
      name: 'activityInviteCodes',
      component: () => import('@/views/_expand/activityList/inviteCodes.vue'),
      meta: {
        title: '活动邀请码',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'club',
      name: 'activityClubMembers',
      meta: {
        title: '常客/俱乐部',
        noCache: true,
      },
      component: () => import('@/views/_expand/activityList/clubMembers.vue'),
    },
    {
      path: 'need',
      name: 'activityNeed',
      meta: {
        title: '寻义工',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      component: () => import('@/views/_expand/activityList/idleEnergyNeed.vue'),
    },
    {
      path: 'place',
      name: 'activityPlace',
      meta: {
        title: '我的场地',
        noCache: true,
        // 场地列表是隐藏菜单；侧栏高亮指到可见的场地控制台
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      component: () => import('@/views/_expand/place/index.vue'),
    },
    {
      path: 'place/desk',
      name: 'activityPlaceDesk',
      component: () => import('@/views/_expand/place/desk.vue'),
      meta: {
        title: '场地控制台',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      hidden: true,
    },
    {
      path: 'place/edit',
      name: 'activityPlaceEdit',
      component: () => import('@/views/_expand/place/place_edit.vue'),
      meta: {
        title: '编辑场地',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      hidden: true,
    },
    {
      path: 'place/menu-tables',
      name: 'activityPlaceMenuTables',
      component: () => import('@/views/_expand/place/menuTables.vue'),
      meta: {
        title: '场地桌码',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      hidden: true,
    },
    {
      path: 'place/browse',
      name: 'CollaborationVenueIntentBrowse',
      component: () => import('@/views/_expand/collaboration/venue-intent/browse.vue'),
      meta: {
        title: '场地库',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
    },
    {
      path: 'place/collaboration',
      name: 'CollaborationVenueIntent',
      meta: {
        title: '场地合约',
        noCache: true,
        // 场地合约从场地控制台进入，侧栏不单列
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      component: () => import('@/views/_expand/collaboration/venue-intent/index.vue'),
    },
    {
      path: 'place/collaboration/edit',
      name: 'CollaborationVenueIntentEdit',
      component: () => import('@/views/_expand/collaboration/venue-intent/edit.vue'),
      meta: {
        title: '编辑场地意向',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      hidden: true,
    },
    {
      path: 'place/slot',
      name: 'CollaborationRentalSlot',
      meta: {
        title: '场地档期',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      component: () => import('@/views/_expand/collaboration/rental-slot/index.vue'),
    },
    {
      path: 'place/slot/edit',
      name: 'CollaborationRentalSlotEdit',
      component: () => import('@/views/_expand/collaboration/rental-slot/edit.vue'),
      meta: {
        title: '编辑开放档期',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      hidden: true,
    },
    {
      path: 'place/slot/batch',
      name: 'CollaborationRentalSlotBatch',
      component: () => import('@/views/_expand/collaboration/rental-slot/batch.vue'),
      meta: {
        title: '开放日历',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      hidden: true,
    },
    {
      path: 'place/slot/zones',
      name: 'CollaborationPlaceZone',
      component: () => import('@/views/_expand/collaboration/rental-slot/zones.vue'),
      meta: {
        title: '场地分区',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      hidden: true,
    },
    {
      path: 'settlement',
      name: 'CollaborationSettlement',
      meta: {
        title: '合约收支',
        noCache: true,
      },
      component: () => import('@/views/_expand/collaboration/settlement/index.vue'),
    },
    {
      path: 'show-calendar',
      name: 'ResidentShowCalendar',
      meta: {
        title: '节目排期',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      component: () => import('@/views/_expand/collaboration/show-calendar/index.vue'),
    },
    {
      path: 'show-series',
      name: 'ResidentShowSeries',
      meta: {
        title: '票价模板',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      component: () => import('@/views/_expand/collaboration/show-series/index.vue'),
    },
    {
      path: 'show-series/edit/:id',
      name: 'ResidentShowSeriesEdit',
      component: () => import('@/views/_expand/collaboration/show-series/edit.vue'),
      meta: {
        title: '编辑票价',
        noCache: true,
        activeMenu: `${roterPre}/activity/desk`,
      },
      hidden: true,
    },
    {
      path: 'invite-grid',
      name: 'activityInviteGrid',
      meta: {
        title: '卡座/包间预约',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      component: () => import('@/views/_expand/invite-grid/index.vue'),
    },
    {
      path: 'invite-grid/edit',
      name: 'activityInviteGridEdit',
      component: () => import('@/views/_expand/invite-grid/edit.vue'),
      meta: {
        title: '配置预约方案',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      hidden: true,
    },
    {
      path: 'invite-grid/board',
      name: 'activityInviteGridBoard',
      component: () => import('@/views/_expand/invite-grid/board.vue'),
      meta: {
        title: '分格占用看板',
        noCache: true,
        activeMenu: `${roterPre}/activity/place/desk`,
      },
      hidden: true,
    },
  ],
};

export default activityRouter;
